import React, { useState, useEffect, type ReactNode } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebar } from '@/contexts/SidebarContext';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/shadcn/tooltip';

type Props = {
  label: string;
  icon?: string;
  active?: boolean;
  hasChildren?: boolean;
  defaultOpen?: boolean;
  to?: string;
  children?: ReactNode;
  collapsed?: boolean;
};

const MenuItem = ({
  label,
  icon,
  active,
  hasChildren,
  defaultOpen = false,
  to,
  children,
  collapsed,
}: Props) => {
  const location = useLocation();
  const sidebar = useSidebar();
  const isCollapsed = collapsed !== undefined ? collapsed : sidebar.isCollapsed;

  // exact match for parent `to`, prefix match for children
  const matchExact = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const matchPrefix = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // compute active status (for initial open state and for render-time styling)
  const computeIsActive = () => {
    // use exact match for this item's `to` so parent won't stay active when a child route is open
    let activeFlag = !!active || (to ? matchExact(to) : false);
    if (!activeFlag && hasChildren && children) {
      React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        const childTo = (child.props as any).to as string | undefined;
        if (childTo && matchPrefix(childTo)) activeFlag = true;
      });
    }
    return activeFlag;
  };

  const [open, setOpen] = useState<boolean>(() => defaultOpen || computeIsActive());

  const isActive = computeIsActive();

  // keep open state in sync with active status: open when active, collapse when not active
  useEffect(() => {
    if (hasChildren) setOpen(isActive);
  }, [isActive, hasChildren]);

  const baseClass = isCollapsed
    ? `w-11 h-11 flex items-center justify-center rounded-xl text-base transition-all duration-150 relative cursor-pointer ${
        isActive
          ? 'bg-[#FFF5EE] text-[#FC480D] font-semibold shadow-2xs'
          : 'text-gray-600 hover:bg-[#F6F6F6] hover:text-[#0E1726]'
      }`
    : `w-full h-11 flex items-center gap-2.5 px-3 rounded-xl text-sm transition-all duration-150 cursor-pointer ${
        isActive
          ? 'bg-[#FFF5EE] text-[#FC480D] font-semibold'
          : 'text-gray-600 hover:bg-[#F6F6F6] hover:text-[#0E1726] font-medium'
      }`;

  if (hasChildren) {
    // clone children to inject active class when their `to` matches
    const renderedChildren = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      const childTo = (child.props as any).to as string | undefined;
      const childClass = (child.props as any).className ?? '';
      const childActive = childTo ? matchPrefix(childTo) : false;
      // always add hover styles; when active also add bg and text color
      const added =
        `${childClass} hover:bg-primary-50 hover:text-primary-500 ${childActive ? 'bg-primary-50 text-primary-500 font-medium' : ''}`.trim();
      return React.cloneElement(child as React.ReactElement<any>, { className: added });
    });

    if (isCollapsed) {
      return (
        <div className="relative group flex justify-center w-full">
          <button type="button" className={baseClass} aria-label={label}>
            <span className="text-xl flex items-center justify-center">
              {icon && <Icon icon={icon} className="w-5 h-5" />}
            </span>
          </button>

          {/* Flyout Submenu on Hover when Collapsed */}
          <div className="absolute left-[calc(100%+8px)] top-0 hidden group-hover:block z-50 min-w-[180px] pointer-events-auto">
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 space-y-1">
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 mb-1">
                {label}
              </div>
              {renderedChildren}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <button type="button" onClick={() => setOpen((s) => !s)} className={baseClass}>
          <span className="text-lg">{icon && <Icon icon={icon} className="w-5 h-5" />}</span>
          <span className="truncate">{label}</span>
          <span
            className={`ml-auto text-xs text-gray-400 transform transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <Icon icon="mdi:chevron-right" className="w-5 h-5" />
          </span>
        </button>

        {open && <div className="mt-2 ml-8 space-y-1">{renderedChildren}</div>}
      </div>
    );
  }

  // No children: render as Link when `to` is provided, otherwise as a div/button
  if (to) {
    const linkElement = (
      <Link to={to} className={baseClass} aria-label={label}>
        <span className="text-lg flex items-center justify-center">
          {icon && <Icon icon={icon} className="w-5 h-5" />}
        </span>
        {!isCollapsed && <span className="truncate">{label}</span>}
      </Link>
    );

    if (isCollapsed) {
      return (
        <div className="flex justify-center w-full">
          <Tooltip>
            <TooltipTrigger asChild>{linkElement}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={12}>
              {label}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return <div className="flex justify-center w-full">{linkElement}</div>;
  }

  const divElement = (
    <div className={baseClass} aria-label={label}>
      <span className="text-lg flex items-center justify-center">
        {icon && <Icon icon={icon} className="w-5 h-5" />}
      </span>
      {!isCollapsed && <span className="truncate">{label}</span>}
    </div>
  );

  if (isCollapsed) {
    return (
      <div className="flex justify-center w-full">
        <Tooltip>
          <TooltipTrigger asChild>{divElement}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            {label}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return <div className="flex justify-center w-full">{divElement}</div>;
};

export default MenuItem;
